import _ from 'lodash';
import { ProposalMutation } from '@/store/proposal/proposal.mutation';
import { Proposal } from '@/model/proposal';
import { ProposalStatus, isEditableStatus } from '@/model/proposal-status';
import { ProposalAction } from '@/store/proposal/proposal.action';
import proposalsService from '@/services/proposals.service';
import { ActionContext, Commit } from 'vuex';
import { RootState } from '@/store';
import { SalePrice } from '@/model/sale-price';
import { ProposalSalePrice } from '@/model/proposal-sale-price';
import { Paragraph } from '@/store/proposal/content/paragraph';
import { AxiosResponse } from 'axios';
import proposalsMapper from '@/utils/proposals.mapper';
import { AppMutation } from '@/store/app/app.mutation';
import { NotificationType } from '@/store/app/notification/notification-type';
import { ProposalContent } from '@/model/proposal-content';
import { ProposalCustomer } from '@/model/proposal-customer';
import { ProposalExportType } from '@/model/proposal-export-type';
import defaultContent from '@/mocks/default-content.mock';
import suppliersService from '@/services/suppliers.service';
import {
    StringifySupplierParams,
    SupplierFields, SupplierParams,
    SuppliersChoices,
    SuppliersSection,
    SuppliersSectionSavingData,
    SuppliersSelectOption,
} from '@/model/suppliers-choices';
import { Supplier } from '@/model/supplier';
import countriesService from '@/services/countries.service';
import productsService from '@/services/products.service';
import contractsService from '@/services/contracts.service';
import servicesService from '@/services/services.service';
import disclaimersService from '@/services/disclaimers.service';
import { ProductSalePrice } from '@/model/product-sale-price';
import { ProductCostPrice } from '@/model/product-cost-price';

interface ParagraphMutationData {
    newParagraph: Paragraph,
    oldParagraph: Paragraph
}

export interface SalePriceColumn {
    isFirst: boolean;
    weightBracket: SalePrice;
}

export interface ZonedCostPrices {
    zone?: string;
    weightBrackets: Map<number, SalePriceColumn>;
}

export interface SalePriceRow {
    id: string;
    country: string;
    supplier: string;
    supplierId?: string;
    product: string;
    contract: string;
    service: string;
    description?: string,
    weightBrackets: Map<number, SalePriceColumn>;
    zonedWeightBrackets?: ZonedCostPrices[]; // Multiple cost price rows grouped by zone
    selected: boolean;
    grossMargin: number;
    productId: string;
    zone?: string;
    new?: boolean;
}

export interface ReducedSalePriceRow {
    prices: SalePrice[],
    description?: string,
    new?: true,
}

export interface ProposalState {
    suppliersChoices: SuppliersChoices;
    allCountries: SuppliersSelectOption[];
    showGrossMargin: boolean;
    showCostPrice: boolean;
    displayList: boolean;
    proposal: Proposal;
    formProcessing: boolean;
    blockGrossMargin: boolean;
    weightBrackets: Set<number>;
    salePricesRows: SalePriceRow[];
    returnsWeightBrackets: Set<number>;
    returnsRows: SalePriceRow[];
    surchargesRows: SalePriceRow[];
    availableSurcharges: ProductCostPrice[];
    isLoading: boolean;
    content: ProposalContent;
    isProcessing: boolean;
    proposalList: ProposalCustomer[];
    listSearch: string;
    includeArchived: boolean;
    listLoaded: boolean;
}

export const state: ProposalState = {
    suppliersChoices: {
        availableSuppliers: [],
        suppliers: [],
    },
    allCountries: [],
    showGrossMargin: true,
    showCostPrice: true,
    displayList: false,
    formProcessing: false,
    proposal: {
        id: undefined,
        createdAt: '',
        updatedAt: '',
        sentAt: '',
        approvedAt: '',
        rejectedAt: '',
        validFrom: '',
        validTo: '',
        updatedBy: '',
        createdBy: '',
        requestedBy: '',
        status: ProposalStatus.DRAFT,
        description: '',
        suppliers: '',
        pricesChanged: false,
    },
    isProcessing: false,
    blockGrossMargin: true,
    weightBrackets: new Set<number>(),
    salePricesRows: [],
    returnsWeightBrackets: new Set<number>(),
    returnsRows: [],
    surchargesRows: [],
    availableSurcharges: [],
    isLoading: false,
    content: {...defaultContent},
    proposalList: [],
    listSearch: '',
    includeArchived: false,
    listLoaded: false,
};

// The weight-bracket columns are the set of weightMax boundaries across the rows; recompute it
// from the rows that remain after an add/remove so empty columns disappear.
function bracketsFromRows(rows: SalePriceRow[]): Set<number> {
    return new Set(
        rows
            .flatMap(row => Array.from(row.weightBrackets.keys()))
            .sort((a, b) => a - b)
    );
}

// Refresh the live "Prices" disclaimer for the given supplier sections (it is derived from the
// selected suppliers/products). Linehaul and General disclaimer are user-editable per proposal and
// the backend returns the same global setting regardless of suppliers, so they are intentionally
// left untouched here — overwriting them would wipe manual edits.
// Carriers that currently have at least one outbound/returns line. A carrier counts as "emptied"
// — and so loses its surcharges — only if it was in this set before a removal and is gone after.
// Carriers that only ever contribute surcharges (a cost price list of surcharges alone) are never
// in the set, so they are never pruned.
function shipmentSupplierIds(state: ProposalState): Set<string> {
    return new Set(
        [...state.salePricesRows, ...state.returnsRows]
            .map(row => row.supplierId)
            .filter((id): id is string => id !== undefined)
    );
}

async function refreshDisclaimers(commit: Commit, sections: SuppliersSection[]): Promise<void> {
    const { data } = await disclaimersService.getWithParams(sections);

    commit(ProposalMutation.SET_CONTENT_DISCLAIMERS, {
        newParagraph: { title: 'Prices', content: proposalsMapper.mapDynamicDisclaimers(data.outbound) }
    });
}

/** Surface prices that lost their zone at import time. Call after the mappers have run. */
function reportBracketCollisions(commit: Commit): void {
    const collisions = proposalsMapper.takeBracketCollisions();
    if (0 === collisions.length) {
        return;
    }

    commit(AppMutation.ADD_NOTIFICATION, {
        type: NotificationType.WARNING,
        title: 'proposal.general.notifications.zone_collision_title',
        content: 'proposal.general.notifications.zone_collision',
        params: { rows: collisions.join(', ') }
    }, { root: true });
}

export const actions = {
    [ProposalAction.DELETE_PROPOSAL](
        context: ActionContext<ProposalState, RootState>,
        proposalId: string
    ): Promise<AxiosResponse<void>> {
        context.commit(ProposalMutation.SET_IS_PROCESSING, true);
        context.commit(ProposalMutation.SET_LIST_LOADED, false);

        return proposalsService
            .destroy(proposalId)
            .finally(() => context.commit(ProposalMutation.SET_IS_PROCESSING, false))
    },
    [ProposalAction.EXPORT_PROPOSAL](
        context: ActionContext<ProposalState, RootState>,
        data: { proposalId: string, type: ProposalExportType, forImport?: boolean }
    ): Promise<AxiosResponse<string>> {
        context.commit(ProposalMutation.SET_IS_PROCESSING, true);

        return proposalsService.export(data.proposalId, data.type, data.forImport || false)
            .then(response => {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(new Blob([response.data]));
                link.setAttribute('download', `Proposal - ${data.proposalId}.${data.type.toLowerCase()}`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                return response;
            })
            .finally(() => context.commit(ProposalMutation.SET_IS_PROCESSING, false))
    },
    [ProposalAction.FETCH_LIST_ELEMENTS](
        context: ActionContext<ProposalState, RootState>
    ): Promise<AxiosResponse<ProposalCustomer[]>> {
        context.commit(ProposalMutation.SET_IS_PROCESSING, true);

        return proposalsService
            .getList(context.state.includeArchived)
            .then(response => {
                context.commit(ProposalMutation.SET_PROPOSAL_LIST, response.data)
                context.commit(ProposalMutation.SET_LIST_LOADED, true)
                return response;
            })
            .finally(() => context.commit(ProposalMutation.SET_IS_PROCESSING, false));
    },
    [ProposalAction.CHANGE_STATUS](
        context: ActionContext<ProposalState, RootState>,
        data: { proposalId: string, status: ProposalStatus }
    ): Promise<AxiosResponse<ProposalSalePrice>> {
        context.commit(ProposalMutation.SET_IS_PROCESSING, true);
        context.commit(ProposalMutation.SET_LIST_LOADED, false);

        return proposalsService.changeStatus(data.proposalId, data.status)
            .finally(() => context.commit(ProposalMutation.SET_IS_PROCESSING, false));
    },
    [ProposalAction.REPOPULATE](
        context: ActionContext<ProposalState, RootState>,
        data: { proposalId: string }
    ): Promise<AxiosResponse<ProposalSalePrice>> {
       context.commit(ProposalMutation.SET_IS_PROCESSING, true);

      return proposalsService.repopulate(data.proposalId)
          .finally(() => context.commit(ProposalMutation.SET_IS_PROCESSING, false));
    },
    async [ProposalAction.LOAD_PROPOSAL_AVAILABLE_SUPPLIERS](
        {commit}: ActionContext<ProposalState, RootState>,
        payload: string | { customerId: string; countryIds?: string }
    ): Promise<void> {
        // Support both old string format and new object format for backwards compatibility
        const customerId = typeof payload === 'string' ? payload : payload.customerId;
        const countryIds = typeof payload === 'string' ? undefined : payload.countryIds;

        return suppliersService
            .getWithParams(customerId, countryIds)
            .then(({data}) => {
                commit(
                    ProposalMutation.SET_PROPOSAL_AVAILABLE_SUPPLIERS,
                    _.map(
                        data,
                        (supplierData)=> ({
                            id: supplierData.id.toString(),
                            name: supplierData.fullName,
                        })
                    )
                );
            });
    },
    async [ProposalAction.LOAD_PROPOSAL_ALL_COUNTRIES](
        {commit}: ActionContext<ProposalState, RootState>
    ): Promise<void> {
        return countriesService
            .getCountries()
            .then(({data}) => {
                commit(
                    ProposalMutation.SET_PROPOSAL_ALL_COUNTRIES,
                    _.map(
                        data,
                        (countryData)=> ({
                            id: countryData.id?.toString(),
                            name: countryData.name,
                        })
                    )
                );
            });
    },
    async [ProposalAction.LOAD_PROPOSAL_AVAILABLE_COUNTRIES](
        {state, commit}: ActionContext<ProposalState, RootState>,
        index: number
    ): Promise<void> {
        return countriesService
            .getWithParams(state.suppliersChoices.suppliers[index].params)
            .then(({data})=> {
                commit(
                    ProposalMutation.SET_PROPOSAL_AVAILABLE_COUNTRIES,
                    {
                        index,
                        countries: _.map(
                            data,
                            (countryData)=> ({
                                id: countryData.id?.toString(),
                                name: countryData.name,
                            })
                        ),
                    }
                );
                commit(
                    ProposalMutation.SAVE_PROPOSAL_SUPPLIERS_SECTION,
                    {
                        index,
                        values: {
                            ...state.suppliersChoices.suppliers[index].params,
                            [SupplierFields.countries]: _.filter(
                                state.suppliersChoices.suppliers[index].params[SupplierFields.countries],
                                (id) => _.includes(_.map(data, (country) => country.id?.toString()), id.toString()),
                            )
                        },
                    }
                );
            });
    },
    async [ProposalAction.LOAD_PROPOSAL_AVAILABLE_PRODUCTS](
        {state, commit}: ActionContext<ProposalState, RootState>,
        index: number
    ): Promise<void> {
        return productsService
            .getWithParams(state.suppliersChoices.suppliers[index].params)
            .then(({data})=> {
                commit(
                    ProposalMutation.SET_PROPOSAL_AVAILABLE_PRODUCTS,
                    {
                        index,
                        products: _.map(
                            data,
                            (productData)=> ({
                                id: productData.name,
                                name: productData.name,
                            })
                        ),
                    }
                );
                commit(
                    ProposalMutation.SAVE_PROPOSAL_SUPPLIERS_SECTION,
                    {
                        index,
                        values: {
                            ...state.suppliersChoices.suppliers[index].params,
                            [SupplierFields.products]: _.filter(
                                state.suppliersChoices.suppliers[index].params[SupplierFields.products],
                                (id) => _.includes(_.map(data, 'name'), id)
                            )
                        },
                    }
                );
            });
    },
    async [ProposalAction.LOAD_PROPOSAL_AVAILABLE_CONTRACTS](
        {state, commit}: ActionContext<ProposalState, RootState>,
        index: number
    ): Promise<void> {
        return contractsService
            .getWithParams(state.suppliersChoices.suppliers[index].params)
            .then(({data})=> {
                commit(
                    ProposalMutation.SET_PROPOSAL_AVAILABLE_CONTRACTS,
                    {
                        index,
                        contracts: _.map(
                            data,
                            (contractData)=> ({
                                id: contractData.name,
                                name: contractData.name,
                            })
                        ),
                    }
                );
                commit(
                    ProposalMutation.SAVE_PROPOSAL_SUPPLIERS_SECTION,
                    {
                        index,
                        values: {
                            ...state.suppliersChoices.suppliers[index].params,
                            [SupplierFields.contracts]: _.filter(
                                state.suppliersChoices.suppliers[index].params[SupplierFields.contracts],
                                (id) => _.includes(_.map(data, 'name'), id)
                            )
                        },
                    }
                );
            });
    },
    async [ProposalAction.LOAD_PROPOSAL_AVAILABLE_SERVICES](
        {state, commit}: ActionContext<ProposalState, RootState>,
        index: number
    ): Promise<void> {
        return servicesService
            .getWithParams(state.suppliersChoices.suppliers[index].params)
            .then(({data})=> {
                commit(
                    ProposalMutation.SET_PROPOSAL_AVAILABLE_SERVICES,
                    {
                        index,
                        services: _.map(
                            data,
                            (serviceData)=> ({
                                id: serviceData.name,
                                name: serviceData.name,
                            })
                        ),
                    }
                );
                commit(
                    ProposalMutation.SAVE_PROPOSAL_SUPPLIERS_SECTION,
                    {
                        index,
                        values: {
                            ...state.suppliersChoices.suppliers[index].params,
                            [SupplierFields.services]: _.filter(
                                state.suppliersChoices.suppliers[index].params[SupplierFields.services],
                                (id) => _.includes(_.map(data, 'name'), id)
                            )
                        },
                    }
                );
            });
    },
    async [ ProposalAction.LOAD_PROPOSAL_SUPPLIERS_CHOICES ](
        {commit, dispatch}: ActionContext<ProposalState, RootState>,
        data: {
            customerId: string;
            suppliersSelection: StringifySupplierParams[];
        }
    ): Promise<void> {
        commit(ProposalMutation.CLEAR_PROPOSAL_SUPPLIERS_CHOICES);
        await dispatch(ProposalAction.LOAD_PROPOSAL_AVAILABLE_SUPPLIERS, data.customerId)
        _.each(data.suppliersSelection, async (params, index) => {
            commit(ProposalMutation.ADD_PROPOSAL_SUPPLIERS_SECTION);
            commit(ProposalMutation.SAVE_PROPOSAL_SUPPLIERS_SECTION, {
                index,
                values: _.reduce(
                    params,
                    (
                        preparedValues,
                        stringValues,
                        key
                    ) => {
                        const values = stringValues?.split(',');
                        if (values?.length) {
                            preparedValues[ key as SupplierFields ] = values;
                        }
                        return preparedValues;
                    },
                    {} as SupplierParams
                ),
            });
            await dispatch(ProposalAction.LOAD_PROPOSAL_AVAILABLE_COUNTRIES, index);
            await dispatch(ProposalAction.LOAD_PROPOSAL_AVAILABLE_PRODUCTS, index);
            await dispatch(ProposalAction.LOAD_PROPOSAL_AVAILABLE_CONTRACTS, index);
            await dispatch(ProposalAction.LOAD_PROPOSAL_AVAILABLE_SERVICES, index);
        });
    },
    [ProposalAction.SAVE_NEW_PROPOSAL](
        {state, commit}: ActionContext<ProposalState, RootState>,
        customerId: string
    ): Promise<AxiosResponse<ProposalSalePrice>> {
        commit(ProposalMutation.SET_LIST_LOADED, false);
        return proposalsService
            .create(customerId, {
                validFrom: state.proposal.validFrom,
                validTo: state.proposal.validTo,
                description: state.proposal.description || '',
                createdBy: state.proposal.createdBy || '',
                requestedBy: state.proposal.requestedBy || '',
                productList: proposalsMapper.mapProductListPrices(state.salePricesRows, state.returnsRows, state.surchargesRows),
                suppliers: proposalsMapper.mapSuppliersParams(state.suppliersChoices.suppliers),
                content: state.content
            });
    },
    [ProposalAction.UPDATE_PROPOSAL]({state, commit}: ActionContext<ProposalState, RootState>, proposalId: string): Promise<AxiosResponse<ProposalSalePrice>> {
        commit(ProposalMutation.SET_LIST_LOADED, false);
        return proposalsService
            .update(proposalId, {
                validFrom: state.proposal.validFrom,
                validTo: state.proposal.validTo,
                description: state.proposal.description || '',
                createdBy: state.proposal.createdBy || '',
                requestedBy: state.proposal.requestedBy || '',
                productList: proposalsMapper.mapUpdateProductsListPrices(state.salePricesRows, state.returnsRows, state.surchargesRows),
                suppliers: proposalsMapper.mapSuppliersParams(state.suppliersChoices.suppliers),
                content: state.content
            })
    },
    [ProposalAction.UPDATE_PROPOSAL_DESCRIPTION](
        {commit}: ActionContext<ProposalState, RootState>,
        payload: { proposalId: string, description: string }
    ): Promise<void> {
        return proposalsService
            .updateDescription(payload.proposalId, payload.description)
            .then(() => commit(ProposalMutation.SET_PROPOSAL_DESCRIPTION, payload.description));
    },
    [ProposalAction.LOAD_SALE_PRICES_ROWS_FOR_PREVIEW]({commit, dispatch}: ActionContext<ProposalState, RootState>, ids: Record<string, string>): void {
        commit(ProposalMutation.SET_IS_LOADING, true);

        proposalsService
            .getOne(ids.proposalId)
            .then(async ({data}) => {
                const weightBrackets = proposalsMapper.mapSalePriceBrackets(data.products.outbound);
                const returnsBrackets = proposalsMapper.mapSalePriceBrackets(data.products.returns);

                commit(ProposalMutation.SET_WEIGHT_BRACKETS, weightBrackets);
                commit(ProposalMutation.SET_RETURNS_WEIGHT_BRACKETS, returnsBrackets);
                commit(ProposalMutation.SET_PROPOSAL, data);
                commit(ProposalMutation.SET_PROPOSAL_CONTENT, data.content);
                commit(ProposalMutation.SET_PROPOSAL_VALID_TO, data.validTo);
                commit(ProposalMutation.SET_PROPOSAL_VALID_FROM, data.validFrom);

                await dispatch(ProposalAction.LOAD_PROPOSAL_SUPPLIERS_CHOICES, {
                    customerId: ids.customerId,
                    suppliersSelection: data.suppliersSelection
                });

                commit(
                    ProposalMutation.SET_SALE_PRICES_ROWS,
                    proposalsMapper.mapSalePricesProducts(data.products.outbound, weightBrackets, data.status)
                );

                commit(
                    ProposalMutation.SET_RETURNS_SALE_PRICES_ROWS,
                    proposalsMapper.mapSalePricesProducts(data.products.returns, returnsBrackets, data.status)
                );

                commit(
                    ProposalMutation.SET_SURCHARGES_SALE_PRICES_ROWS,
                    proposalsMapper.mapSalePricesSurcharges(data.products.surcharges)
                );

                reportBracketCollisions(commit);

            })
            .finally(() => commit(ProposalMutation.SET_IS_LOADING, false));
    },
    [ProposalAction.LOAD_SALE_PRICES_ROWS_FOR_REUSING_PROPOSAL]({commit, state}: ActionContext<ProposalState, RootState>, data: { proposalId: string, loadAllProducts: boolean, keepSalePrices: boolean }): void {
        commit(ProposalMutation.SET_IS_LOADING, true);

        proposalsService
            .getForReuse(data.proposalId, data.loadAllProducts, data.keepSalePrices)
            .then(async ({data}) => {
                const weightBrackets = proposalsMapper.mapSalePriceBrackets(data.products.outbound);
                const returnsBrackets = proposalsMapper.mapSalePriceBrackets(data.products.returns);

                commit(ProposalMutation.SET_WEIGHT_BRACKETS, weightBrackets);
                commit(ProposalMutation.SET_RETURNS_WEIGHT_BRACKETS, returnsBrackets);
                commit(ProposalMutation.SET_PROPOSAL_CONTENT, data.content);
                commit(ProposalMutation.SET_PROPOSAL_VALID_TO, data.validTo);
                commit(ProposalMutation.SET_PROPOSAL_VALID_FROM, data.validFrom);

                commit(
                    ProposalMutation.SET_SALE_PRICES_ROWS,
                    proposalsMapper.mapSalePricesProducts(data.products.outbound, weightBrackets, data.status)
                );

                commit(
                    ProposalMutation.SET_RETURNS_SALE_PRICES_ROWS,
                    proposalsMapper.mapSalePricesProducts(data.products.returns, returnsBrackets, data.status)
                );

                commit(
                    ProposalMutation.SET_SURCHARGES_SALE_PRICES_ROWS,
                    proposalsMapper.mapSalePricesSurcharges(data.products.surcharges)
                );

                reportBracketCollisions(commit);

                commit(ProposalMutation.SET_PROPOSAL_CONTENT, {...defaultContent});

                const disclaimers = await disclaimersService.getWithParams(state.suppliersChoices.suppliers);
                const linehaul = disclaimers.data.linehaul
                const generalDisclaimer = disclaimers.data.generalDisclaimer

                commit(
                  ProposalMutation.SET_CONTENT_DISCLAIMERS,
                  {
                      newParagraph: {
                          title: data.content.outboundDisclaimer.title,
                          content: data.content.outboundDisclaimer.content,
                      }
                  }
              );

                commit(
                    ProposalMutation.SET_CONTENT_LINEHAUL,
                    {
                        newParagraph: {
                            title: 'Linehaul',
                            content: linehaul.content,
                        }
                    }
                );

                commit(
                    ProposalMutation.SET_CONTENT_GENERAL_DISCLAIMER,
                    {
                        newParagraph: {
                            title: 'General disclaimer',
                            content: generalDisclaimer.content,
                        }
                    }
                );
            })
            .finally(() => commit(ProposalMutation.SET_IS_LOADING, false));
    },
    [ProposalAction.GET_SALE_PRICES_ROWS]({commit, state}: ActionContext<ProposalState, RootState>, customerId: string): void {
        commit(ProposalMutation.SET_IS_LOADING, true);
        proposalsService
            .getCostPrices(
                customerId,
                proposalsMapper.mapSuppliersParams(state.suppliersChoices.suppliers)
            )
            .then(async ({data}) => {
                const weightBrackets = proposalsMapper.mapCostPriceBrackets(data.outbound);
                const returnsBrackets = proposalsMapper.mapCostPriceBrackets(data.returns);

                commit(ProposalMutation.SET_WEIGHT_BRACKETS, weightBrackets);
                commit(ProposalMutation.SET_RETURNS_WEIGHT_BRACKETS, returnsBrackets);
                commit(ProposalMutation.SET_PROPOSAL_VALID_TO, data.defaults.validTo);
                commit(ProposalMutation.SET_PROPOSAL_VALID_FROM, data.defaults.validFrom);

                commit(
                    ProposalMutation.SET_SALE_PRICES_ROWS,
                    proposalsMapper.mapCostPricesProducts(data.outbound, weightBrackets)
                );
                commit(
                    ProposalMutation.SET_RETURNS_SALE_PRICES_ROWS,
                    proposalsMapper.mapCostPricesProducts(data.returns, returnsBrackets)
                );
                commit(
                    ProposalMutation.SET_SURCHARGES_SALE_PRICES_ROWS,
                    proposalsMapper.mapCostPricesSurcharges(data.surcharges)
                );

                reportBracketCollisions(commit);

                commit(ProposalMutation.SET_PROPOSAL_CONTENT, {...defaultContent});

                const disclaimers = await disclaimersService.getWithParams(state.suppliersChoices.suppliers);
                const linehaul = disclaimers.data.linehaul
                const generalDisclaimer = disclaimers.data.generalDisclaimer

                commit(
                    ProposalMutation.SET_CONTENT_DISCLAIMERS,
                    {
                        newParagraph: {
                            title: 'Prices',
                            content: proposalsMapper.mapDynamicDisclaimers(disclaimers.data.outbound),
                        }
                    }
                );
                commit(
                    ProposalMutation.SET_CONTENT_LINEHAUL,
                    {
                        newParagraph: {
                            title: 'Linehaul',
                            content: linehaul.content,
                        }
                    }
                );
                commit(
                    ProposalMutation.SET_CONTENT_GENERAL_DISCLAIMER,
                    {
                        newParagraph: {
                            title: 'General disclaimer',
                            content: generalDisclaimer.content,
                        }
                    }
                );
            })
            .catch((error) => {
              if (error.response.status === 422) {
                alert('There are no prices for the selected suppliers');
              }
              })
            .finally(() => commit(ProposalMutation.SET_IS_LOADING, false));
    },
    // Append-only "Add products": fetch cost prices for the chosen sections and merge them into
    // the existing rows (whole new lines, or just the missing weight brackets on existing lines).
    // Existing lines/margins are never touched. Returns a summary for the result notification.
    async [ProposalAction.ADD_SALE_PRICES_ROWS](
        {commit, state}: ActionContext<ProposalState, RootState>,
        payload: { customerId: string; sections: SuppliersSection[] }
    ): Promise<{ outbound: number; returns: number; surcharges: number; brackets: number; skipped: string[] }> {
        commit(ProposalMutation.SET_IS_LOADING, true);
        try {
            const {data} = await proposalsService.getCostPrices(
                payload.customerId,
                proposalsMapper.mapSuppliersParams(payload.sections)
            );

            const outbound = proposalsMapper.mergeAddedCostPriceRows(state.salePricesRows, state.weightBrackets, data.outbound);
            commit(ProposalMutation.SET_WEIGHT_BRACKETS, outbound.brackets);
            commit(ProposalMutation.SET_SALE_PRICES_ROWS, outbound.rows);

            const returns = proposalsMapper.mergeAddedCostPriceRows(state.returnsRows, state.returnsWeightBrackets, data.returns);
            commit(ProposalMutation.SET_RETURNS_WEIGHT_BRACKETS, returns.brackets);
            commit(ProposalMutation.SET_RETURNS_SALE_PRICES_ROWS, returns.rows);

            const surcharges = proposalsMapper.mergeAddedSurchargeRows(state.surchargesRows, data.surcharges);
            commit(ProposalMutation.SET_SURCHARGES_SALE_PRICES_ROWS, surcharges.rows);

            await refreshDisclaimers(commit, state.suppliersChoices.suppliers);

            return {
                outbound: outbound.addedRows,
                returns: returns.addedRows,
                surcharges: surcharges.addedRows,
                brackets: outbound.addedBrackets + returns.addedBrackets,
                skipped: Array.from(new Set([...outbound.skipped, ...returns.skipped, ...surcharges.skipped]))
            };
        } finally {
            commit(ProposalMutation.SET_IS_LOADING, false);
        }
    },
    // Bulk-remove selected product lines (and, for fully-removed carriers, their surcharges and
    // disclaimers). Persisted when the proposal is saved.
    async [ProposalAction.REMOVE_PRODUCTS](
        {commit, dispatch, state}: ActionContext<ProposalState, RootState>,
        payload: { rowIds: string[]; fullSupplierIds: string[] }
    ): Promise<void> {
        const shipmentSuppliersBefore = Array.from(shipmentSupplierIds(state));
        commit(ProposalMutation.REMOVE_PRODUCTS_ROWS, payload);
        await dispatch(ProposalAction.CLEANUP_EMPTY_SUPPLIERS, shipmentSuppliersBefore);
    },
    // Remove the rows ticked in the price tables (the per-row waste bin), then run the same
    // cascade as the Remove products dialog.
    async [ProposalAction.REMOVE_SELECTED_ROWS](
        {commit, dispatch, state}: ActionContext<ProposalState, RootState>
    ): Promise<void> {
        const shipmentSuppliersBefore = Array.from(shipmentSupplierIds(state));
        commit(ProposalMutation.REMOVE_SALE_PRICES_ROW);
        await dispatch(ProposalAction.CLEANUP_EMPTY_SUPPLIERS, shipmentSuppliersBefore);
    },
    // Drop surcharges + disclaimers for any carrier whose last outbound/returns line was just
    // removed, then refresh the disclaimer preview. Runs after every removal path (Remove products
    // dialog and the per-row delete) so the cascade is consistent however a carrier is emptied.
    // `shipmentSuppliersBefore` is the carrier set captured *before* the removal — without it a
    // surcharge-only carrier would look permanently orphaned and lose its surcharges on any delete.
    async [ProposalAction.CLEANUP_EMPTY_SUPPLIERS](
        {commit, state}: ActionContext<ProposalState, RootState>,
        shipmentSuppliersBefore: string[]
    ): Promise<void> {
        commit(ProposalMutation.PRUNE_ORPHANED_SUPPLIERS, shipmentSuppliersBefore);
        await refreshDisclaimers(commit, state.suppliersChoices.suppliers);
    },
    async [ProposalAction.LOAD_SETTINGS_DISCLAIMERS]({commit, state}: ActionContext<ProposalState, RootState>): Promise<void> {
        try {
            const disclaimers = await disclaimersService.getWithParams(state.suppliersChoices.suppliers);
            const linehaul = disclaimers.data.linehaul;
            const generalDisclaimer = disclaimers.data.generalDisclaimer;

            // Only pre-fill from the global settings template when the proposal has no value yet
            // (e.g. a brand-new proposal). Never overwrite text the user has already saved/edited —
            // doing so wipes manual edits when the Content tab is re-opened.
            if (linehaul && !state.content.linehaul?.content) {
                commit(
                    ProposalMutation.SET_CONTENT_LINEHAUL,
                    {
                        newParagraph: {
                            title: 'Linehaul',
                            content: linehaul.content,
                        }
                    }
                );
            }

            if (generalDisclaimer && !state.content.generalDisclaimer?.content) {
                commit(
                    ProposalMutation.SET_CONTENT_GENERAL_DISCLAIMER,
                    {
                        newParagraph: {
                            title: 'General disclaimer',
                            content: generalDisclaimer.content,
                        }
                    }
                );
            }
        } catch (error) {
            console.error('Failed to load settings disclaimers:', error);
        }
    },
    async [ProposalAction.LOAD_AVAILABLE_SURCHARGES](
        {commit}: ActionContext<ProposalState, RootState>,
        proposalId: string
    ): Promise<void> {
        return proposalsService
            .getAvailableSurcharges(proposalId)
            .then(({data}) => {
                commit(ProposalMutation.SET_AVAILABLE_SURCHARGES, data);
            });
    }
};

export const mutations = {
    [ProposalMutation.SET_PROPOSAL_DESCRIPTION](state: ProposalState, description: string): void {
        state.proposal.description = description;
    },
    [ProposalMutation.SET_PROPOSAL_VALID_FROM](state: ProposalState, validFrom: string): void {
        state.proposal.validFrom = validFrom;
    },
    [ProposalMutation.SET_PROPOSAL_VALID_TO](state: ProposalState, validTo: string): void {
        state.proposal.validTo = validTo
    },
    [ProposalMutation.SET_PROPOSAL_CREATED_BY](state: ProposalState, createdBy: string): void {
        state.proposal.createdBy = createdBy;
    },
    [ProposalMutation.SET_PROPOSAL_REQUESTED_BY](state: ProposalState, requestedBy: string): void {
        state.proposal.requestedBy = requestedBy;
    },
    [ProposalMutation.SET_IS_PROCESSING](state: ProposalState, isProcessing: boolean): void {
        state.isProcessing = isProcessing;
    },
    [ProposalMutation.SET_SALE_PRICES_ROWS](state: ProposalState, rows: SalePriceRow[]): void {
        state.salePricesRows = rows;
    },
    [ProposalMutation.SET_RETURNS_SALE_PRICES_ROWS](state: ProposalState, rows: SalePriceRow[]): void {
        state.returnsRows = rows;
    },
    [ProposalMutation.SET_SURCHARGES_SALE_PRICES_ROWS](state: ProposalState, rows: SalePriceRow[]): void {
        state.surchargesRows = rows;
    },
    [ProposalMutation.SET_GROSS_MARGIN](state: ProposalState, showGrossMargin: boolean): void {
        state.showGrossMargin = showGrossMargin;
    },
    [ProposalMutation.SET_COST_PRICE](state: ProposalState, showCostPrice: boolean): void {
        state.showCostPrice = showCostPrice;
    },
    [ProposalMutation.SET_DISPLAY_LIST](state: ProposalState, displayList: boolean): void {
        state.displayList = displayList;
    },
    [ProposalMutation.SET_FORM_PROCESSING](state: ProposalState, isProcessing: boolean): void {
        state.formProcessing = isProcessing;
    },
    [ProposalMutation.SET_BLOCK_GROSS_MARGIN](state: ProposalState, isBlocked: boolean): void {
        state.blockGrossMargin = isBlocked;
    },
    [ProposalMutation.SET_WEIGHT_BRACKETS](state: ProposalState, brackets: Set<number>): void {
        state.weightBrackets = brackets;
    },
    [ProposalMutation.SET_RETURNS_WEIGHT_BRACKETS](state: ProposalState, brackets: Set<number>): void {
        state.returnsWeightBrackets = brackets;
    },
    [ProposalMutation.ADD_CONTENT_PARAGRAPH](state: ProposalState, paragraph: Paragraph): void {
        state.content.paragraphs = [...state.content.paragraphs, paragraph];
    },
    [ProposalMutation.SET_IS_LOADING](state: ProposalState, isLoading: boolean): void {
        state.isLoading = isLoading;
    },
    [ProposalMutation.REMOVE_SALE_PRICES_ROW](state: ProposalState): void {
        state.salePricesRows = state.salePricesRows.filter(row => !row.selected);
        state.returnsRows = state.returnsRows.filter(row => !row.selected);
        state.surchargesRows = state.surchargesRows.filter(row => !row.selected);
    },
    [ProposalMutation.SET_PROPOSAL_AVAILABLE_SUPPLIERS](state: ProposalState, suppliers: Supplier[]): void {
        state.suppliersChoices.availableSuppliers = suppliers;
    },
    [ProposalMutation.SET_PROPOSAL_ALL_COUNTRIES](state: ProposalState, countries: SuppliersSelectOption[]): void {
        state.allCountries = countries;
    },
    [ProposalMutation.SET_PROPOSAL_AVAILABLE_COUNTRIES](state: ProposalState, data: {index: number; countries: SuppliersSelectOption[]}): void {
        state.suppliersChoices.suppliers[data.index].availableOptions[SupplierFields.countries] = data.countries;
    },
    [ProposalMutation.SET_PROPOSAL_AVAILABLE_PRODUCTS](state: ProposalState, data: {index: number; products: SuppliersSelectOption[]}): void {
        state.suppliersChoices.suppliers[data.index].availableOptions[SupplierFields.products] = data.products;
    },
    [ProposalMutation.SET_PROPOSAL_AVAILABLE_CONTRACTS](state: ProposalState, data: {index: number; contracts: SuppliersSelectOption[]}): void {
        state.suppliersChoices.suppliers[data.index].availableOptions[SupplierFields.contracts] = data.contracts;
    },
    [ProposalMutation.SET_PROPOSAL_AVAILABLE_SERVICES](state: ProposalState, data: {index: number; services: SuppliersSelectOption[]}): void {
        state.suppliersChoices.suppliers[data.index].availableOptions[SupplierFields.services] = data.services;
    },
    [ProposalMutation.ADD_PROPOSAL_SUPPLIERS_SECTION](state: ProposalState): void {
        state.suppliersChoices.suppliers.push({
            params: {},
            availableOptions: {},
        });
    },
    [ProposalMutation.REMOVE_PROPOSAL_SUPPLIERS_SECTION](state: ProposalState, sectionIndex: number): void {
        state.suppliersChoices.suppliers.splice(sectionIndex, 1);
    },
    [ProposalMutation.SAVE_PROPOSAL_SUPPLIERS_SECTION](state: ProposalState, sectionData: SuppliersSectionSavingData): void {
      // I want to pass the previousYear value to the store
        console.log('sectionData', sectionData);
        state.suppliersChoices.suppliers[sectionData.index].params = { ...sectionData.values };
    },
    // Replace the supplier sections wholesale (keeps the loaded availableSuppliers list). Used by
    // the Add-products modal to swap in a blank section and later restore + append the new one.
    [ProposalMutation.SET_PROPOSAL_SUPPLIERS_SECTIONS](state: ProposalState, sections: SuppliersSection[]): void {
        state.suppliersChoices.suppliers = sections;
    },
    [ProposalMutation.REMOVE_PRODUCTS_ROWS](
        state: ProposalState,
        payload: { rowIds: string[]; fullSupplierIds: string[] }
    ): void {
        const rowIds = new Set(payload.rowIds);
        const fullSupplierIds = new Set(payload.fullSupplierIds);

        state.salePricesRows = state.salePricesRows.filter(row => !rowIds.has(row.id));
        state.returnsRows = state.returnsRows.filter(row => !rowIds.has(row.id));
        // A removed carrier loses its surcharges too; individual product removals leave them.
        state.surchargesRows = state.surchargesRows.filter(
            row => !(row.supplierId !== undefined && fullSupplierIds.has(row.supplierId))
        );

        state.weightBrackets = bracketsFromRows(state.salePricesRows);
        state.returnsWeightBrackets = bracketsFromRows(state.returnsRows);

        state.suppliersChoices.suppliers = state.suppliersChoices.suppliers.filter(section => {
            const ids = section.params[SupplierFields.suppliers] ?? [];
            return !ids.some(id => fullSupplierIds.has(id));
        });
    },
    // After a removal, a carrier that had outbound/returns lines and has none left should also lose
    // its surcharges and disclaimers. The comparison is against `shipmentSuppliersBefore` (captured
    // before the removal), so a carrier that never had outbound/returns lines at all — a
    // surcharge-only cost price list — is left untouched. The disclaimer is regenerated from the
    // remaining sections by the calling action.
    [ProposalMutation.PRUNE_ORPHANED_SUPPLIERS](state: ProposalState, shipmentSuppliersBefore: string[] = []): void {
        const activeSupplierIds = shipmentSupplierIds(state);
        const emptiedSupplierIds = new Set(
            shipmentSuppliersBefore.filter(id => !activeSupplierIds.has(id))
        );

        if (!emptiedSupplierIds.size) {
            return;
        }

        state.surchargesRows = state.surchargesRows.filter(
            row => row.supplierId === undefined || !emptiedSupplierIds.has(row.supplierId)
        );

        state.suppliersChoices.suppliers = state.suppliersChoices.suppliers.filter(section => {
            const ids = section.params[SupplierFields.suppliers] ?? [];
            return ids.length === 0 || !ids.every(id => emptiedSupplierIds.has(id));
        });
    },
    [ProposalMutation.CLEAR_PROPOSAL_SUPPLIERS_CHOICES](state: ProposalState): void {
        state.suppliersChoices = {
            availableSuppliers: [],
            suppliers: [],
        };
    },
    [ProposalMutation.SET_PROPOSAL](state: ProposalState, proposal: Proposal): void {
        state.proposal = proposal;
    },
    [ProposalMutation.CLEAR_PROPOSAL](state: ProposalState): void {
        state.proposal = {
            id: undefined,
            createdAt: '',
            updatedAt: '',
            sentAt: '',
            approvedAt: '',
            rejectedAt: '',
            validFrom: '',
            validTo: '',
            updatedBy: '',
            createdBy: '',
            requestedBy: '',
            description: '',
            status: ProposalStatus.DRAFT,
            suppliers: '',
            pricesChanged: false,
        };
    },
    [ProposalMutation.CLEAR_SALE_PRICES](state: ProposalState): void {
        state.salePricesRows = [];
        state.returnsRows = [];
        state.surchargesRows = [];
        state.weightBrackets = new Set<number>();
        state.returnsWeightBrackets = new Set<number>();
        state.availableSurcharges = [];
    },
    [ProposalMutation.SETUP_GROSS_MARGIN](state: ProposalState, grossMargin: number): void {
        state.salePricesRows.forEach(
            row => row.weightBrackets.forEach(column => {
                const amount = column.weightBracket.costPrice.amount;

                if (amount === null) {
                  return;
                }

                column.weightBracket.grossMargin = grossMargin;
                const amountt = Number(amount);
                column.weightBracket.price.amount = Math.round(amountt * (grossMargin + 100) / 100);
            })
        );
        state.returnsRows.forEach(
            row => row.weightBrackets.forEach(column => {
                const amount = column.weightBracket.costPrice.amount;

                if (amount === null) {
                  return;
                }

                column.weightBracket.grossMargin = grossMargin;
                const amountt = Number(amount);
                column.weightBracket.price.amount = Math.round(amountt * (grossMargin + 100) / 100);
            })
        );
        state.surchargesRows.forEach(
            row => row.weightBrackets.forEach(column => {
                const amount = column.weightBracket.costPrice.amount;

                if (amount === null) {
                  return;
                }

                column.weightBracket.grossMargin = grossMargin;
                const amountt = Number(amount);
                column.weightBracket.price.amount = Math.round(amountt * (grossMargin + 100) / 100);
            })
        );
    },
    [ProposalMutation.SETUP_OUTBOUND_GROSS_MARGIN](state: ProposalState, grossMargin: number): void {
        state.salePricesRows.forEach(row =>
            row.weightBrackets.forEach(column => {
              const weightBracket = column.weightBracket;

              const costPrice = weightBracket.costPrice.amount;
              if (costPrice === null) {
                return;
              }

              weightBracket.grossMargin = grossMargin;
              const costPriceNumber = Number(costPrice);
              weightBracket.price.amount = Math.round(costPriceNumber * (grossMargin + 100) / 100);
            })
          );
    },
    [ProposalMutation.SETUP_RETURNS_GROSS_MARGIN](state: ProposalState, grossMargin: number): void {
        state.returnsRows.forEach(row =>
            row.weightBrackets.forEach(column => {
                const weightBracket = column.weightBracket;

                const costPrice = weightBracket.costPrice.amount;
                if (costPrice === null) {
                return;
                }

                weightBracket.grossMargin = grossMargin;
                const costPriceNumber = Number(costPrice);
                weightBracket.price.amount = Math.round(costPriceNumber * (grossMargin + 100) / 100);
            })
            );
    },
    [ProposalMutation.SETUP_SURCHARGES_GROSS_MARGIN](state: ProposalState, grossMargin: number): void {
        state.surchargesRows.forEach(row =>
            row.weightBrackets.forEach(column => {
                const weightBracket = column.weightBracket;

                const costPrice = weightBracket.costPrice.amount;
                if (costPrice === null) {
                return;
                }

                weightBracket.grossMargin = grossMargin;
                const costPriceNumber = Number(costPrice);
                weightBracket.price.amount = Math.round(costPriceNumber * (grossMargin + 100) / 100);
            })
            );
    },
    [ProposalMutation.SET_CONTENT_INTRO_TEXT](state: ProposalState, data: ParagraphMutationData): void {
        state.content.introText = data.newParagraph;
    },
    [ProposalMutation.SET_CONTENT_GENERAL_DISCLAIMER](state: ProposalState, data: ParagraphMutationData): void {
        state.content.generalDisclaimer = data.newParagraph;
    },
    [ProposalMutation.SET_CONTENT_DISCLAIMERS](state: ProposalState, data: ParagraphMutationData): void {
        console.log('data', data);
        state.content.outboundDisclaimer = data.newParagraph;
    },
    [ProposalMutation.SET_CONTENT_TERMS](state: ProposalState, data: ParagraphMutationData): void {
        state.content.terms = data.newParagraph;
    },
    [ProposalMutation.SET_CONTENT_LINEHAUL](state: ProposalState, data: ParagraphMutationData): void {
        state.content.linehaul = data.newParagraph;
    },
    [ProposalMutation.REMOVE_PARAGRAPH](state: ProposalState, paragraph: Paragraph): void {
        state.content.paragraphs = state.content.paragraphs.filter(element => element !== paragraph);
    },
    [ProposalMutation.CHANGE_PARAGRAPH](state: ProposalState, data: ParagraphMutationData): void {
        state.content.paragraphs = state.content.paragraphs.map(paragraph => {
            if (paragraph === data.oldParagraph) {
                return data.newParagraph;
            }

            return paragraph;
        })
    },
    [ProposalMutation.SET_CONTENT_GDPR](state: ProposalState, data: ParagraphMutationData): void {
        state.content.gdpr = data.newParagraph;
    },
    [ProposalMutation.SET_PROPOSAL_CONTENT](state: ProposalState, content: ProposalContent): void {
        state.content = content;
    },
    [ProposalMutation.SET_PROPOSAL_LIST](state: ProposalState, elements: ProposalCustomer[]): void {
        state.proposalList = elements;
    },
    [ProposalMutation.SET_AVAILABLE_SURCHARGES](state: ProposalState, surcharges: ProductCostPrice[]): void {
        state.availableSurcharges = surcharges;
    },
    [ProposalMutation.ADD_SURCHARGE_ROW](state: ProposalState, surcharge: ProductCostPrice): void {
        const weightBrackets = new Map<number, SalePriceColumn>();
        weightBrackets.set(0, {
            isFirst: true,
            weightBracket: {
                priceId: surcharge.costPrices[0].priceId,
                weightMin: surcharge.costPrices[0].weightMin,
                weightMax: surcharge.costPrices[0].weightMax,
                price: {
                    currency: surcharge.costPrices[0].price.currency,
                    amount: surcharge.costPrices[0].price.amount
                },
                costPrice: surcharge.costPrices[0].price,
                grossMargin: 0,
                productId: surcharge.id
            }
        });
        state.surchargesRows.push({
            id: surcharge.id,
            country: surcharge.country.name,
            supplier: surcharge.supplier,
            supplierId: surcharge.supplierId?.toString(),
            product: surcharge.name,
            contract: surcharge.contract,
            service: surcharge.service,
            zone: undefined,
            productId: surcharge.id,
            description: surcharge.description,
            weightBrackets,
            selected: false,
            grossMargin: 0,
            new: true
        });
    },
    [ProposalMutation.ADD_SALE_PRICE_SURCHARGE_ROW](state: ProposalState, product: ProductSalePrice): void {
        const rows = proposalsMapper.mapSalePricesSurcharges([product]);
        if (rows.length) {
            state.surchargesRows.push(rows[0]);
        }
    },
    [ProposalMutation.SET_LIST_SEARCH](state: ProposalState, search: string): void {
        state.listSearch = search;
    },
    [ProposalMutation.SET_INCLUDE_ARCHIVED](state: ProposalState, includeArchived: boolean): void {
        state.includeArchived = includeArchived;
    },
    [ProposalMutation.SET_LIST_LOADED](state: ProposalState, listLoaded: boolean): void {
        state.listLoaded = listLoaded;
    }
};

export const getters = {
    showGrossMargin(state: ProposalState): boolean {
        return state.showGrossMargin;
    },
    showCostPrice(state: ProposalState): boolean {
        return state.showCostPrice;
    },
    suppliersChoices(state: ProposalState): SuppliersChoices {
        return state.suppliersChoices;
    },
    allCountries(state: ProposalState): SuppliersSelectOption[] {
        return state.allCountries;
    },
    proposal(state: ProposalState): Proposal {
        return state.proposal;
    },
    blockGrossMargin(state: ProposalState): boolean {
        return state.blockGrossMargin;
    },
    salePricesRows(state: ProposalState): SalePriceRow[] {
        return state.salePricesRows;
    },
    weightBrackets(state: ProposalState): Set<number> {
        return state.weightBrackets;
    },
    isProposalLoading(state: ProposalState): boolean {
        return state.isLoading;
    },
    introText(state: ProposalState): Paragraph {
        return state.content.introText;
    },
    generalDisclaimer(state: ProposalState): Paragraph {
        return state.content.generalDisclaimer;
    },
    disclaimers(state: ProposalState): Paragraph {
        console.log('state.content.outboundDisclaimer', state.content.outboundDisclaimer);
        state.content.outboundDisclaimer.title = 'Prices';
        
        state.content.outboundDisclaimer.content += state.content.returnsDisclaimer.content; 
        return state.content.outboundDisclaimer;
    },
    returnsDisclaimers(state: ProposalState): Paragraph {
        return state.content.returnsDisclaimer;
    },
    terms(state: ProposalState): Paragraph {
        return state.content.terms;
    },
    linehaul(state: ProposalState): Paragraph {
        return state.content.linehaul;
    },
    gdpr(state: ProposalState): Paragraph {
        return state.content.gdpr
    },
    returnsRows(state: ProposalState): SalePriceRow[] {
        return state.returnsRows;
    },
    returnsWeightBrackets(state: ProposalState): Set<number> {
        return state.returnsWeightBrackets;
    },
    surchargesRows(state: ProposalState): SalePriceRow[] {
        return state.surchargesRows;
    },
    paragraphs(state: ProposalState): Paragraph[] {
        return state.content.paragraphs;
    },
    isProposalEditable(state: ProposalState): boolean {
        return isEditableStatus(state.proposal.status);
    },
    isProposalsProcessing(state: ProposalState): boolean {
        return state.isProcessing;
    },
    proposalList(state: ProposalState): ProposalCustomer[] {
        return state.proposalList;
    },
    listSearch(state: ProposalState): string {
        return state.listSearch;
    },
    includeArchived(state: ProposalState): boolean {
        return state.includeArchived;
    },
    listLoaded(state: ProposalState): boolean {
        return state.listLoaded;
    },
    proposalValidFrom(state: ProposalState): string | null {
        return state.proposal.validFrom;
    },
    proposalValidTo(state: ProposalState): string | null {
        return state.proposal.validTo;
    },
    availableSurcharges(state: ProposalState): ProductCostPrice[] {
        return state.availableSurcharges;
    },
    proposalSuppliers(state: ProposalState): SuppliersSelectOption[] {
        const selectedIds = new Set(
            state.suppliersChoices.suppliers
                .flatMap(section => section.params[SupplierFields.suppliers] || [])
        );
        return (state.suppliersChoices.availableSuppliers as unknown as SuppliersSelectOption[])
            .filter(s => selectedIds.has(s.id));
    }
};

export default {
    state,
    actions,
    mutations,
    getters
}
